import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  Alert,
  Box,
  CircularProgress,
  Typography,
  Button,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import { palette } from "../../styles/theme";
import { useSelector } from "../../common/hooks/useSelector";
import { specsEditorActions } from "../../common/slices/specsEditor/slice";
import type { SpecsIconGroup } from "./defaultPartIcons";
import type { SpecsEditorIconPickerTarget } from "./types";
import { SpecsImageUploadTrigger } from "./SpecsEditorFields/index";

export type SpecsEditorIconPickerApi = {
  iconPickerTarget: SpecsEditorIconPickerTarget;
  openIconPicker: (target: Exclude<SpecsEditorIconPickerTarget, null>) => void;
  closeIconPicker: () => void;
};

export function useSpecsEditorIconPickerApi(): SpecsEditorIconPickerApi {
  const dispatch = useDispatch();
  const iconPickerTarget = useSelector(
    (state) => state.specsEditor.iconPickerTarget
  ) as SpecsEditorIconPickerTarget;

  const openIconPicker = useCallback(
    (target: Exclude<SpecsEditorIconPickerTarget, null>) => {
      dispatch(specsEditorActions.openIconPicker(target));
    },
    [dispatch]
  );

  const closeIconPicker = useCallback(() => {
    dispatch(specsEditorActions.closeIconPicker());
  }, [dispatch]);

  return useMemo(
    () => ({
      iconPickerTarget,
      openIconPicker,
      closeIconPicker,
    }),
    [closeIconPicker, iconPickerTarget, openIconPicker]
  );
}

interface IconPickerModalProps {
  open: boolean;
  specs: any;
  onSelect: (url: string) => void;
  onUpload: (file: File) => Promise<void>;
  onClose: () => void;
}

export default function IconPickerModal({
  open,
  specs,
  onSelect,
  onUpload,
  onClose,
}: IconPickerModalProps) {
  const [groups, setGroups] = useState<SpecsIconGroup[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    if (!open || !specs) return;
    import("./defaultPartIcons").then(({ getAllIconsFromSpecs }) => {
      setGroups(getAllIconsFromSpecs(specs));
    });
  }, [open, specs]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ "& .MuiDialog-paper": { maxHeight: "70vh" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>Pick Icon</DialogTitle>
      <DialogContent sx={{ overflowY: "auto" }}>
        {groups.map((group) => (
          <Box key={group.productName} sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
            >
              {group.productName}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {group.icons.map((icon, i) => (
                <Tooltip key={i} title={icon.elementName}>
                  <Box
                    component="img"
                    src={icon.url}
                    alt={icon.elementName}
                    onClick={() => onSelect(icon.url)}
                    sx={{
                      width: 40,
                      height: 40,
                      objectFit: "contain",
                      cursor: "pointer",
                      borderRadius: "4px",
                      border: "1px solid transparent",
                      "&:hover": { borderColor: palette.brand.primary },
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
          </Box>
        ))}
        {groups.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No icons found in specs. Upload a custom SVG below.
          </Typography>
        )}
        <Divider sx={{ my: 1.5 }} />
        <SpecsImageUploadTrigger
          onUpload={(file) => {
            setUploading(true);
            setUploadError("");
            return onUpload(file)
              .catch((error) => {
                setUploadError(
                  error instanceof Error
                    ? error.message
                    : "Failed to upload SVG"
                );
              })
              .finally(() => {
                setUploading(false);
              });
          }}
          uploading={uploading}
          accept=".svg,image/svg+xml"
          title="Upload custom SVG"
          buttonLabel="Upload custom SVG"
          variant="button"
          fullWidth
        />
        {uploadError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {uploadError}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
      </DialogActions>
    </Dialog>
  );
}
